import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Task } from '@/types/tasks';
import DashboardModal from '@/components/dashboard-modal';
import TaskForm from './form/TaskForm';
import { useConfirmationDialog } from '@/hooks/ui/use-confirmation-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useTaskContextTracking } from '@/hooks/tasks/useTaskContextTracking';

interface EditTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onDeleteTask?: (e: React.MouseEvent) => void;
  // NEW: Context-aware props following New Task system pattern
  context?: 'task-manager' | 'project';
  showAssignees?: boolean;
  hideGoalSelector?: boolean;
}

export interface TaskFormValues {
  title: string;
  description: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'not-important' | null;
  goalId?: string | null;
  assigneeIds?: string[];
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({
  isOpen,
  onClose,
  task,
  onUpdateTask,
  onDeleteTask,
  context = 'task-manager', // Default to task-manager context
  showAssignees,
  hideGoalSelector,
}) => {
  const { showConfirmation, ConfirmationDialog } = useConfirmationDialog();
  
  // Context-based defaults following New Task system pattern
  const finalShowAssignees = showAssignees !== undefined 
    ? showAssignees 
    : context === 'project'; // Only show assignees in project context
    
  const finalHideGoalSelector = hideGoalSelector !== undefined
    ? hideGoalSelector
    : false; // Always show goal selector in both contexts

  // Stable empty array reference to prevent infinite loops
  const EMPTY_ASSIGNEES: any[] = useMemo(() => [], []);
  
  // Use direct PostgREST query with new foreign key relationship
  const { data: currentAssignees = EMPTY_ASSIGNEES, isLoading: assigneesLoading } = useQuery({
    queryKey: ['task-assignees', task?.id],
    queryFn: async (): Promise<any[]> => {
      if (!task?.id) return EMPTY_ASSIGNEES;
      
      // Use the new foreign key constraint to get assignee data
      const { data, error } = await supabase
        .from('planner_tasks_assignees')
        .select(`
          id,
          assignee_id,
          assigned_at,
          assigned_by,
          public_profile!fk_planner_tasks_assignees_assignee_profile(
            id,
            first_name,
            username,
            profile_picture_url
          )
        `)
        .eq('task_id', task.id)
        .order('assigned_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching task assignees:', error);
        return EMPTY_ASSIGNEES;
      }
      
      // Transform to match TaskAssignee interface
      return (data || []).map(item => ({
        assignment_id: item.id,
        assignee_id: item.assignee_id,
        first_name: item.public_profile?.first_name || '',
        username: item.public_profile?.username || '',
        profile_picture_url: item.public_profile?.profile_picture_url,
        display_name: item.public_profile?.first_name || item.public_profile?.username || 'Anonymous',
        assigned_at: item.assigned_at,
        assigned_by_name: 'Unknown' // We'd need another join for this, but it's not critical
      }));
    },
    enabled: finalShowAssignees && !!task?.goalId && !!task?.id, // Only load assignees when needed
    staleTime: 300000, // 5 minutes
    gcTime: 300000
  });
  
  // Detect if we're in a project context to hide goal selector
  const isInProjectContext = context === 'project';
  const [formData, setFormData] = useState<TaskFormValues>({
    title: '',
    description: '',
    dueDate: '',
    priority: null,
    goalId: null,
    assigneeIds: [],
  });

  // Stabilize assigneeIds to prevent infinite loops - task-manager always uses empty array
  const assigneeIds = useMemo(() => 
    finalShowAssignees ? currentAssignees.map(a => a.assignee_id) : [], 
    [finalShowAssignees, currentAssignees]
  );

  // Single useEffect for all form data updates - prevents infinite loops
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        dueDate: task.dueDate || '',
        priority: task.priority,
        goalId: task.goalId || null,
        assigneeIds: assigneeIds, // Now stable reference
      });
    }
  }, [
    task?.id, 
    task?.title, 
    task?.description, 
    task?.dueDate, 
    task?.priority, 
    task?.goalId,
    assigneeIds  // Now safe to include - stable reference
  ]);

  // Use context tracking for task changes
  const { trackTaskChange, canPerformOperation } = useTaskContextTracking();

  const handleSubmit = async (values: TaskFormValues) => {
    if (!task || assigneesLoading) return;
    
    // Check if we can perform the operation
    if (!canPerformOperation('edit')) {
      // This is a soft check - we'll still allow edits but warn about potential conflicts
      console.warn('[EditTaskModal] Context processing in progress, proceeding with edit');
    }
    
    try {
      // Handle task field updates (exclude assigneeIds from task update since they're handled immediately)
      const { assigneeIds, ...taskUpdates } = values;
      const updates: Partial<Task> = {
        ...taskUpdates,
      };
      
      // Check if any meaningful changes were made
      const hasChanges = Object.keys(taskUpdates).some(key => {
        const taskKey = key as keyof Task;
        return task[taskKey] !== taskUpdates[taskKey as keyof typeof taskUpdates];
      });
      
      // Update task fields
      onUpdateTask(task.id, updates);
      
      // Track the edit for context ONLY for project tasks (team tasks)
      // Check both goalId AND context to ensure we're in a project
      if (task.goalId && hasChanges && context === 'project') {
        await trackTaskChange(task.id, 'edit');
        console.log('[EditTaskModal] Tracked TEAM task edit for context', {
          taskId: task.id,
          goalId: task.goalId,
          context: context
        });
      } else if (hasChanges) {
        console.log('[EditTaskModal] Task edit NOT tracked', {
          taskId: task.id,
          hasGoalId: !!task.goalId,
          goalId: task.goalId,
          context: context,
          reason: !task.goalId ? 'No goalId' : context !== 'project' ? 'Not in project context (personal task)' : 'No changes'
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to update task:', error);
      // Don't close the modal if there was an error
    }
  };

  const handleDeleteWithConfirmation = async () => {
    if (!onDeleteTask || !task) return;

    const confirmed = await showConfirmation({
      title: 'Delete Task',
      description: `Are you sure you want to delete "${task.title}"? This action cannot be undone.`,
      confirmText: 'Delete Forever',
      cancelText: 'Keep Task',
      confirmVariant: 'red',
      cancelVariant: 'green'
    });

    if (confirmed) {
      // Create a synthetic mouse event
      const syntheticEvent = {
        stopPropagation: () => {},
        preventDefault: () => {},
        currentTarget: null,
        target: null,
      } as React.MouseEvent;
      
      onDeleteTask(syntheticEvent);
      onClose();
    }
  };

  // Context-aware title generation
  const getModalTitle = () => {
    return context === 'project' ? 'Edit Team Task' : 'Edit Your Task';
  };

  return (
    <>
      <DashboardModal
        isOpen={isOpen}
        onClose={onClose}
        title={getModalTitle()}
        size="md"
        accentColor="blue"
      >
        <TaskForm
          initialValues={formData}
          onSubmit={handleSubmit}
          onCancel={onClose}
          submitButtonText={assigneesLoading ? "Loading..." : "Update"}
          hideGoalSelector={finalHideGoalSelector}
          showAssignees={finalShowAssignees}
          isLoadingAssignees={assigneesLoading}
          taskId={task?.id}
          onDelete={onDeleteTask ? handleDeleteWithConfirmation : undefined}
        />
      </DashboardModal>

      {/* Confirmation Dialog for Task Deletion */}
      <ConfirmationDialog />
    </>
  );
};

export default EditTaskModal;
